import { useCallback, useState } from 'react';
import type { CaptureFakeLeadResult } from '@/application/use-cases/CaptureFakeLeadUseCase';
import { CaptureFakeLeadUseCase } from '@/application/use-cases/CaptureFakeLeadUseCase';
import { EnqueueLeadUseCase } from '@/application/use-cases/EnqueueLeadUseCase';
import { IndexedDbLeadQueueRepository } from '@/infrastructure/repositories/IndexedDbLeadQueueRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { FakeLeadInput } from '@/domain/entities/FakeLead';

const captureFakeLeadUseCase = new CaptureFakeLeadUseCase(
  new EnqueueLeadUseCase(new IndexedDbLeadQueueRepository())
);

type CaptureLeadStatus = 'idle' | 'loading' | 'error';

interface UseFakeLeadCaptureResult {
  status: CaptureLeadStatus;
  errorMessage: string | null;
  captureLead: (
    session: DeviceSession,
    input: FakeLeadInput,
    consentTextVersion: number
  ) => Promise<CaptureFakeLeadResult>;
}

/**
 * Encapsula a captura do lead fictício (HUB-147) no evento autenticado.
 * Desde a Fatia 2 (HUB-236), a captura grava o lead na `lead_queue` do
 * IndexedDB (`CaptureFakeLeadUseCase` -> `EnqueueLeadUseCase`, CA-01) — não
 * há mais chamada de rede síncrona neste passo (a sincronização real é
 * assíncrona, via `SyncLeadQueueUseCase`).
 *
 * Devolve o resultado completo do use case para que o chamador distinga
 * recusa de consentimento (`success: true, leadName: null`) de erro real —
 * HUB-201. `consentTextVersion` é resolvido pelo chamador a partir do
 * consentimento carregado (HUB-218).
 */
export function useFakeLeadCapture(): UseFakeLeadCaptureResult {
  const [status, setStatus] = useState<CaptureLeadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const captureLead = useCallback(
    async (
      session: DeviceSession,
      input: FakeLeadInput,
      consentTextVersion: number
    ): Promise<CaptureFakeLeadResult> => {
      setStatus('loading');
      setErrorMessage(null);

      const result = await captureFakeLeadUseCase.execute(session, input, consentTextVersion);

      if (!result.success) {
        setStatus('error');
        // Fila cheia (CA-09) tem tela dedicada (`QueueFullModal`); IndexedDB
        // indisponível (CA-03) não expõe nada em UI — só erro de validação
        // de input vira mensagem inline no formulário.
        setErrorMessage(result.reason === 'invalid-input' ? result.error : null);
        return result;
      }

      setStatus('idle');
      return result;
    },
    []
  );

  return { status, errorMessage, captureLead };
}
